import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useSeo } from "@/hooks/useSeo";
import { api, apiError } from "@/lib/api";
import { waChatLink } from "@/lib/whatsapp";
import { FileBadge, HeartPulse, Crown, Loader2, CheckCircle2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const ICONS = { visa: FileBadge, medical: HeartPulse, vip: Crown };

export default function Services() {
  const { t, user } = useApp();
  useSeo({ title: t.seo.services[0], description: t.seo.services[1] });
  const s = t.services;
  const [params] = useSearchParams();
  const [service, setService] = useState(params.get("type") || "visa");
  const [form, setForm] = useState({ name: user?.name || "", phone: "", email: user?.email || "", nationality: "", destination: "", travel_date: "", travelers: 1, notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputCls = "w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/60 focus:border-tigris focus:ring-2 focus:ring-tigris/20 outline-none transition text-sm";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/service-requests", { ...form, service, email: form.email || null, travelers: Number(form.travelers) });
      setDone(data);
      toast.success(s.success);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-sand min-h-screen" data-testid="services-page">
      <section className="bg-tigris text-white px-4 sm:px-6 pt-12 pb-16">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold font-display mb-3">{s.title}</h1>
          <p className="text-white/80 max-w-2xl">{s.sub}</p>
        </div>
      </section>
      <section className="max-w-5xl mx-auto px-4 sm:px-6 -mt-8 pb-20 grid lg:grid-cols-[320px_1fr] gap-6 items-start">
        <div className="space-y-3">
          {Object.keys(ICONS).map((k) => {
            const Icon = ICONS[k];
            return (
              <button key={k} data-testid={`service-type-${k}`} onClick={() => { setService(k); setDone(null); }}
                className={`w-full text-start bg-white rounded-2xl p-5 border-2 transition-colors ${service === k ? "border-tigris shadow-lg" : "border-transparent shadow-sm hover:border-slate-200"}`}>
                <span className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${service === k ? "bg-tigris text-white" : "bg-tigris/10 text-tigris"}`}><Icon size={22} /></span>
                <div className="font-bold text-slate-800">{s.types[k]}</div>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{s.desc[k]}</p>
              </button>
            );
          })}
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8">
          {done ? (
            <div className="text-center py-10" data-testid="service-success">
              <CheckCircle2 size={56} className="text-green-500 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-slate-800 mb-2">{s.success}</h2>
              <p className="text-slate-500 text-sm mb-1">{s.refLabel}: <span className="font-mono font-bold text-tigris" data-testid="service-ref">{done.ref}</span></p>
              <p className="text-slate-500 text-sm mb-6">{s.followUp}</p>
              <a href={waChatLink(`${s.waMsg} ${done.ref}`)} target="_blank" rel="noreferrer" data-testid="service-wa-btn"
                className="btn-wa inline-flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-sm"><MessageCircle size={18} /> {t.common.chatWhatsapp}</a>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <h2 className="text-base md:text-lg font-bold text-slate-800">{s.formTitle} — {s.types[service]}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="block text-xs font-semibold text-slate-600 mb-1.5">{s.name}</label>
                  <input data-testid="svc-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} required /></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1.5">{s.phone}</label>
                  <input data-testid="svc-phone" className={inputCls} dir="ltr" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+964..." required /></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1.5">{s.email}</label>
                  <input data-testid="svc-email" type="email" dir="ltr" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1.5">{s.nationality}</label>
                  <input data-testid="svc-nationality" className={inputCls} value={form.nationality} onChange={(e) => set("nationality", e.target.value)} /></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1.5">{service === "medical" ? s.hospital : s.destination}</label>
                  <input data-testid="svc-destination" className={inputCls} value={form.destination} onChange={(e) => set("destination", e.target.value)} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs font-semibold text-slate-600 mb-1.5">{s.date}</label>
                    <input data-testid="svc-date" type="date" className={inputCls} value={form.travel_date} onChange={(e) => set("travel_date", e.target.value)} /></div>
                  <div><label className="block text-xs font-semibold text-slate-600 mb-1.5">{s.travelers}</label>
                    <input data-testid="svc-travelers" type="number" min={1} max={20} className={inputCls} value={form.travelers} onChange={(e) => set("travelers", e.target.value)} /></div>
                </div>
              </div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1.5">{s.notes}</label>
                <textarea data-testid="svc-notes" rows={4} className={inputCls} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder={s.notesPh[service]} /></div>
              <button type="submit" disabled={loading} data-testid="svc-submit" className="btn-gold w-full py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? <Loader2 size={18} className="animate-spin" /> : s.submit}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
